// frontend/src/components/LabManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { ConfirmDialog } from './ConfirmDialog';
import { Capacitor } from '@capacitor/core';
import { DataService } from '../services/DataService';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';

const LabManagement = () => {
  const { user, token, currentLabId, currentViewMode, switchViewMode } = useAuth();
  const toast = useToast();
  const { confirm, confirmDialogProps } = useConfirm();
  const [labName, setLabName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [currentLabDetails, setCurrentLabDetails] = useState(null);
  const [labMembers, setLabMembers] = useState([]);
  const [selectedLabMemberId, setSelectedLabMemberId] = useState('');
  const [activeSubSection, setActiveSubSection] = useState('overview'); // 'overview', 'create-lab', 'join-lab', 'manage-members'
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [generatedJoinCode, setGeneratedJoinCode] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [miceToTransfer, setMiceToTransfer] = useState([]); // This would typically come from MiceManagement
  const [availableMice, setAvailableMice] = useState([]); // To populate the transfer modal
  const [loading, setLoading] = useState(false);
  // `error` is inline for the two things that describe the page or a field:
  // a failed load, and form validation. Create/join/leave/remove/transfer
  // outcomes are action results and go to toasts — which also fixes the
  // transfer messages, previously wiped by resetForm() a line after being set.
  const [error, setError] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  // Fetch current lab details and members when component mounts or lab status changes
  useEffect(() => {
    const fetchLabData = async () => {
      if (!user || !token) return;

      setLoading(true);
      setError('');

      try {
        if (user.lab_id) {
          const [labDetailsRes, labMembersRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/api/labs/me`),
            axios.get(`${process.env.REACT_APP_API_URL}/api/labs/members`)
          ]);
          setCurrentLabDetails(labDetailsRes.data.lab);
          setLabMembers(labMembersRes.data.members);
        } else {
          setCurrentLabDetails(null);
          setLabMembers([]);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load lab data.');
        console.error('Lab data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLabData();
  }, [user, token, currentLabId]); // Re-fetch if currentLabId changes (e.g., user joins a lab)

  // Fetch mice if transfer modal is open (assuming a simplified scenario for mock-up)
  useEffect(() => {
    const fetchMiceForTransfer = async () => {
      if (showTransferModal && token) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/mice?_limit=${MAX_LIST_LIMIT}`);
          setAvailableMice(response.data.data);
        } catch (err) {
          console.error('Failed to fetch mice for transfer:', err);
          setError('Failed to load mice for transfer.');
        }
      }
    };
    fetchMiceForTransfer();
  }, [showTransferModal, token]);

  const resetForm = () => {
    setLabName('');
    setInviteEmail('');
    setJoinCode('');
    setGeneratedJoinCode('');
    setShowInviteForm(false);
    setSelectedLabMemberId('');
    setMiceToTransfer([]);
    setError('');
    setLoading(false);
    setIsTransferring(false);
  };

  const handleCreateLab = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!labName.trim()) {
      setError('Lab name cannot be empty.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/labs/create`,
        { lab_name: labName }
      );
      // The reload below is what makes the new lab visible everywhere; the
      // 1.5 s delay exists so this is readable first.
      toast.success(`Lab "${response.data.lab.lab_name}" created successfully! You are now the admin.`);
      await DataService.resetCacheForScopeChange();
      // Force a reload to update user context everywhere
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create lab.');
      console.error('Create lab error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateJoinCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setGeneratedJoinCode('');

    if (!inviteEmail.trim()) {
      setError('Email cannot be empty.');
      setLoading(false);
      return;
    }
    if (!currentLabDetails || user?.lab_role !== 'admin') {
      setError('You must be a lab administrator to generate join codes.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/labs/generate-join-code`,
        { email: inviteEmail, lab_id: currentLabDetails.id }
      );
      setGeneratedJoinCode(response.data.join_code);
      toast.success(`Join code sent to ${inviteEmail} and displayed below.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate join code.');
      console.error('Generate join code error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinLab = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!joinCode.trim()) {
      setError('Join code cannot be empty.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/labs/join`,
        { join_code: joinCode }
      );
      toast.success(`Successfully joined lab "${response.data.lab_name}"!`);
      // The cache holds only personal-scope rows; the lab's rows have never
      // been pulled. Rebuild before reloading (C28).
      await DataService.resetCacheForScopeChange();
      // Force a reload to update user context and UI state completely
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to join lab. Invalid or expired code.');
      console.error('Join lab error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveLab = async () => {
    if (!await confirm('Are you sure you want to leave this lab? This action cannot be undone.', {
      confirmLabel: 'Leave Lab',
      danger: true
    })) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/labs/leave`, {});
      toast.success(response.data.message);
      // Every lab-owned row just left this user's RLS scope. Those rows stop
      // appearing in the delta rather than arriving as tombstones, so without
      // this the stale copies would stay in Dexie indefinitely (C28).
      await DataService.resetCacheForScopeChange();
      // Force a reload to update user context and UI state completely
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to leave lab.');
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId, memberUsername) => {
    if (!await confirm(`Are you sure you want to remove ${memberUsername} from the lab?`, {
      confirmLabel: 'Remove',
      danger: true
    })) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/labs/remove-member`, { member_id: memberId });
      toast.success(`${memberUsername} has been removed from the lab.`);
      // The admin doing the removing loses nothing from their own scope, but
      // the removed member's rows (their personal mice, visible while they were
      // in the lab) do leave it — so this client rebuilds too. The removed
      // member's OWN device still holds a stale cache until it next syncs; a
      // server-driven signal for that is out of scope here (C28 covers the
      // transitions this client can observe).
      await DataService.resetCacheForScopeChange();
      // Refetch members list
      const labMembersRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/labs/members`);
      setLabMembers(labMembersRes.data.members);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove member.');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchViewMode = (mode) => {
    switchViewMode(mode);
    toast.info(`Switched to ${mode} view mode.`);
  };

  const handleOpenTransferModal = (selectedMouseIds) => {
    setMiceToTransfer(selectedMouseIds);
    setShowTransferModal(true);
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setMiceToTransfer([]);
    setSelectedLabMemberId('');
    setAvailableMice([]);
    resetForm();
  };

  const handleConfirmTransfer = async (e) => {
    e.preventDefault();
    setIsTransferring(true);
    setError('');

    if (miceToTransfer.length === 0 || !selectedLabMemberId) {
      setError('Please select mice and a recipient.');
      setIsTransferring(false);
      return;
    }

    try {
      const payload = {
        mice_to_transfer: miceToTransfer,
        recipient_user_id: selectedLabMemberId
      };
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/mice/transfer`,
        payload
      );
      // One message, not two: a partial transfer read as a green success plus
      // a red error side by side. Both were wiped a line later anyway, because
      // handleCloseTransferModal -> resetForm cleared them.
      if (response.data.failed_count > 0) {
        toast.warn(
          `${response.data.transferred_count} mouse(s) transferred; ` +
          `${response.data.failed_count} failed.`
        );
      } else {
        toast.success(`${response.data.transferred_count} mouse(s) transferred successfully!`);
      }
      handleCloseTransferModal();
      // Optionally re-fetch mice data in MiceManagement component after transfer
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to transfer mice.');
      console.error('Mouse transfer error:', err);
    } finally {
      setIsTransferring(false);
    }
  };

  if (loading && !currentLabDetails && user?.lab_id) return <div className="loading">Loading lab data...</div>;

  return (
    <div className="lab-management">
      {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
      {error && <div className="error">{error}</div>}

      <div className="settings-nav">
        <button
          className={activeSubSection === 'overview' ? 'active' : ''}
          onClick={() => setActiveSubSection('overview')}
        >
          Overview
        </button>
        {!user?.lab_id && (
          <button
            className={activeSubSection === 'create-lab' ? 'active' : ''}
            onClick={() => setActiveSubSection('create-lab')}
          >
            Create Lab
          </button>
        )}
        {!user?.lab_id && (
          <button
            className={activeSubSection === 'join-lab' ? 'active' : ''}
            onClick={() => setActiveSubSection('join-lab')}
          >
            Join Lab
          </button>
        )}
        {user?.lab_id && user?.lab_role === 'admin' && (
          <button
            className={activeSubSection === 'manage-members' ? 'active' : ''}
            onClick={() => setActiveSubSection('manage-members')}
          >
            Manage Members
          </button>
        )}
      </div>

      {activeSubSection === 'overview' && (
        <div className="lab-info-card">
          <h3>Lab Overview</h3>
          {currentLabDetails ? (
            <>
              <p>
                <strong>Lab Name:</strong> {currentLabDetails.lab_name}
              </p>
              <p>
                <strong>Your Role:</strong> {user?.lab_role === 'admin' ? 'Administrator' : 'Member'}
              </p>
              <p>
                <strong>Current View Mode:</strong> {currentViewMode === 'personal' ? 'Personal Data' : 'Lab Data'}
              </p>
              {currentViewMode === 'lab' && (
                <div className="view-only-warning">
                    <p>You are in Lab View. You can see all of your lab's data, but you can only edit your own records.</p>
                </div>
              )}

              <div className="lab-info-buttons">
                {currentViewMode === 'personal' ? (
                  <button
                    onClick={() => handleSwitchViewMode('lab')}
                    className="primary-button"
                  >
                    Switch to Lab View
                  </button>
                ) : (
                  <button
                    onClick={() => handleSwitchViewMode('personal')}
                    className="secondary-button"
                  >
                    Switch to Personal View
                  </button>
                )}
                 <button
                    onClick={handleLeaveLab}
                    className="secondary-button"
                    style={{ backgroundColor: '#dc3545' }}
                    disabled={loading}
                >
                    Leave Lab
                </button>
              </div>

              <div className="lab-members-list">
                <h4>Lab Members ({labMembers.length})</h4>
                {labMembers.length === 0 ? (
                  <p className="no-data">No other members in this lab yet.</p>
                ) : (
                  labMembers.map((member) => (
                    <div key={member.id} className="lab-member-card">
                      <div className="lab-member-info">
                        <h5>{member.first_name} {member.last_name} ({member.username})</h5>
                        <p>{member.email}</p>
                        <p>Role: {member.lab_role === 'admin' ? 'Admin' : 'Member'}</p>
                      </div>
                      <div className="lab-member-actions">
                        {user?.lab_role === 'admin' && member.id !== user.id && (
                        <button
                            onClick={() => handleRemoveMember(member.id, member.username)}
                            className="secondary-button"
                            style={{ backgroundColor: '#dc3545', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                            disabled={loading}
                        >
                            Remove
                        </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <p>You are not currently part of any lab.</p>
              <p>You can create a new lab or join an existing one.</p>
              <div className="lab-info-buttons">
                <button
                  onClick={() => setActiveSubSection('create-lab')}
                  className="primary-button"
                >
                  Create New Lab
                </button>
                <button
                  onClick={() => setActiveSubSection('join-lab')}
                  className="secondary-button"
                >
                  Join Existing Lab
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeSubSection === 'create-lab' && !currentLabDetails && (
        (user?.subscription_tier === 'lab' || user?.subscription_tier === 'enterprise')
          ? (
            <div className="create-lab-card">
              <h4>Create New Lab</h4>
              <p>Be the administrator of your new SqueakBase lab.</p>
              <form onSubmit={handleCreateLab}>
                <div className="form-group">
                  <label htmlFor="labName">Lab Name *</label>
                  <input
                    type="text"
                    id="labName"
                    value={labName}
                    onChange={(e) => setLabName(e.target.value)}
                    placeholder="e.g., Smith Lab"
                    required
                  />
                </div>
                <button type="submit" disabled={loading} className="primary-button">
                  {loading ? 'Creating...' : 'Create Lab'}
                </button>
                <button type="button" onClick={resetForm} className="secondary-button" style={{marginTop: '10px'}}>
                  Cancel
                </button>
              </form>
            </div>
          ) : (
            <div className="create-lab-card">
              <h3>Lab Creation</h3>
              <p>This is a premium feature. Please upgrade to the Lab plan to create your own Lab.</p>
              {Capacitor.isNativePlatform() ? (
                <p className="setting-description">Upgrade at squeakbase.com</p>
              ) : (
                <button
                  onClick={() => window.open('https://shop.squeakbase.com', '_blank', 'noopener,noreferrer')}
                  className="primary-button"
                >
                  Upgrade to Lab
                </button>
              )}
            </div>
          )
      )}


      {activeSubSection === 'join-lab' && !currentLabDetails && (
        <div className="join-lab-card">
          <h4>Join Lab</h4>
          <p>Enter the join code provided by your lab administrator.</p>
          <form onSubmit={handleJoinLab}>
            <div className="form-group">
              <label htmlFor="joinCode">Join Code *</label>
              <input
                type="text"
                id="joinCode"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Enter join code"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="primary-button">
              {loading ? 'Joining...' : 'Join Lab'}
            </button>
            <button type="button" onClick={resetForm} className="secondary-button" style={{marginTop: '10px'}}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {activeSubSection === 'manage-members' && currentLabDetails && user?.lab_role === 'admin' && (
        <div className="manage-members-card">
          <h4>Manage Lab Members</h4>
          <p>Generate join codes to invite new members to your lab.</p>
          <form onSubmit={handleGenerateJoinCode}>
            <div className="form-group">
              <label htmlFor="inviteEmail">Invite User Email *</label>
              <input
                type="email"
                id="inviteEmail"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="primary-button">
              {loading ? 'Generating...' : 'Generate Join Code'}
            </button>
            {generatedJoinCode && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #007bff' }}>
                <strong>Generated Code:</strong> <span style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>{generatedJoinCode}</span>
                <p style={{fontSize: '0.85em', marginTop: '5px'}}>Share this code with the user. It's one-time use and expires in 24 hours.</p>
              </div>
            )}
          </form>

          <div className="lab-members-list" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <h4>Current Lab Members ({labMembers.length})</h4>
            {labMembers.length === 0 ? (
              <p className="no-data">No members in this lab yet.</p>
            ) : (
              labMembers.map((member) => (
                <div key={member.id} className="lab-member-card">
                  <div className="lab-member-info">
                    <h5>{member.first_name} {member.last_name} ({member.username})</h5>
                    <p>{member.email}</p>
                    <p>Role: {member.lab_role === 'admin' ? 'Admin' : 'Member'}</p>
                  </div>
                  {/* Future: Add options to change role or remove member (admin only) */}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Transfer Mice Modal (triggered from MiceManagement) */}
      {showTransferModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
            <h3>Transfer Mice</h3>
            <p>Selected Mice: {miceToTransfer.length}</p>

            {error && <div className="error">{error}</div>}
      
            <form onSubmit={handleConfirmTransfer}>
              <div className="form-group">
                <label htmlFor="recipient">Select Recipient Lab Member *</label>
                <select
                  id="recipient"
                  value={selectedLabMemberId}
                  onChange={(e) => setSelectedLabMemberId(parseInt(e.target.value))}
                  required
                >
                  <option value="">Select a member</option>
                  {labMembers
                    .filter(member => member.id !== user?.id) // Cannot transfer to self
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.first_name} {member.last_name} ({member.username})
                      </option>
                    ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" disabled={isTransferring} className="primary-button">
                  {isTransferring ? 'Transferring...' : 'Confirm Transfer'}
                </button>
                <button type="button" onClick={handleCloseTransferModal} className="secondary-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabManagement;